import { useEffect, useRef } from 'react';
import { Image } from 'react-native';
import { DeviceProfileBranding, DisplayScreen } from '@oolio-group/domain';
import { isVideoUri } from '../../component/Media/isVideoUri';
import useBehaviorSubjectState from '../rxjs/useSubjectState';
import { useStableList } from '../useStableList/useStableList';
import { sessionSubject } from '../../state/sessionObservable';

const screenUris = (screen?: DisplayScreen): string[] => {
  const uris = [
    screen?.logoUri,
    screen?.backgroundUri,
    ...(screen?.assets ?? [])
      .slice()
      .sort((a, b) => a.position - b.position)
      .map(asset => asset.uri),
  ];
  return uris.filter((uri): uri is string => !!uri && !isVideoUri(uri));
};

export const collectDisplayMediaUris = (
  branding?: DeviceProfileBranding,
): string[] => [
  ...new Set([
    ...screenUris(branding?.idleScreen),
    ...screenUris(branding?.cartScreen),
  ]),
];

/**
 * Mounted once above the navigator. Media is configured per device profile, so
 * warming it on the session keeps the idle and cart screens instant on every
 * order instead of refetching on each mount.
 */
export const usePrefetchDisplayMedia = (): void => {
  const { value: session } = useBehaviorSubjectState(sessionSubject);
  const branding = session?.device?.deviceProfile?.customerDisplay;
  const warmed = useRef(new Set<string>());
  const uris = useStableList(collectDisplayMediaUris(branding), uri => uri);

  useEffect(() => {
    uris.forEach(uri => {
      if (warmed.current.has(uri)) return;
      warmed.current.add(uri);
      // Warming is best effort; a failure just means the image loads on demand.
      Image.prefetch(uri).catch(() => undefined);
    });
  }, [uris]);
};
